import React, { useEffect, useMemo, useState } from "react";

import { ZoneWithCells } from "../../types";
import { Gutters } from "./useGutters";

type Props = Readonly<{
  gutters: Gutters;
  height: number;
  pageImage: HTMLImageElement;
  width: number;
  xOffset: number;
  yOffset: number;
  zone: ZoneWithCells;
  zoom: number;
  showTableUI: boolean;
}>;

const useDrawBackground = (
  canvas: HTMLCanvasElement | null,
  image: HTMLImageElement,
  width: number,
  height: number,
  gutters: Gutters,
  xOffset: number,
  yOffset: number,
  zone: ZoneWithCells,
  showTableUI: boolean,
) => {
  useEffect(() => {
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      console.error("Unable to get a canvas context.");
      return;
    }
    try {
      // clear the existing canvas
      context.clearRect(0, 0, width, height);
      // draw the grey background
      context.fillStyle = "lightgrey";
      context.fillRect(0, 0, width, height);
      // draw the table as background image
      const imageScale = image.naturalHeight / 512;
      const gutter = gutters.top.height;
      const renderWidth = gutters.right.x - (gutters.left.x + gutters.left.width);
      const renderHeight = gutters.bottom.y - (gutters.top.y + gutters.top.height);
      context.drawImage(
        image,
        zone.minX * imageScale,
        zone.minY * imageScale,
        (zone.maxX - zone.minX) * imageScale,
        (zone.maxY - zone.minY) * imageScale,
        xOffset + gutter,
        yOffset + gutter,
        renderWidth,
        renderHeight,
      );
      // draw the four gutters
      context.fillStyle = "#666666";
      Object.values(gutters).forEach(g => {
        context.fillRect(g.x, g.y, g.width, g.height);
      });
      // draw the gutter labels
      context.save();
      context.font = "bold 25px monospace";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillStyle = "lightgrey";
      context.fillText(
        " A D D   C O L U M N ",
        gutters.top.x + gutters.top.width / 2,
        gutters.top.y + gutters.top.height / 2 + 2,
        gutters.top.width,
      );
      context.fillText(
        " A D D   C O L U M N ",
        gutters.bottom.x + gutters.bottom.width / 2,
        gutters.bottom.y + gutters.bottom.height / 2 + 2,
        gutters.bottom.width,
      );
      context.save();
      context.translate(
        gutters.left.x + gutters.left.width / 2 + 2,
        gutters.left.y + gutters.left.height / 2,
      );
      context.rotate(-0.5 * Math.PI);
      context.fillText(" A D D   R O W ", 0, 0, gutters.left.height);
      context.restore();
      context.translate(
        gutters.right.x + gutters.right.width / 2 + 2,
        gutters.right.y + gutters.right.height / 2,
      );
      context.rotate(-0.5 * Math.PI);
      context.fillText(" A D D   R O W ", 0, 0, gutters.right.height);
      context.restore();
      // shade the image
      if (showTableUI) {
        context.globalAlpha = 0.5;
        context.fillStyle = "white";
        context.fillRect(xOffset + gutter, yOffset + gutter, renderWidth, renderHeight);
        context.globalAlpha = 1;
      }
    } catch (err) {
      console.error(err);
    }
  }, [
    canvas,
    gutters,
    height,
    image,
    width,
    xOffset,
    yOffset,
    zone.minX,
    zone.minY,
    zone.maxX,
    zone.maxY,
    showTableUI
  ]);
};

const BackgroundCanvas = ({
  gutters,
  height,
  pageImage,
  width,
  xOffset,
  yOffset,
  zone,
  zoom,
  showTableUI
}: Props) => {
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  useDrawBackground(canvasRef, pageImage, width, height, gutters, xOffset, yOffset, zone, showTableUI);
  const canvasStyles = useMemo(
    () => ({
      height: `${height * zoom}px`,
      width: `${width * zoom}px`,
    }),
    [height, width, zoom],
  );
  return <canvas height={height} ref={setCanvasRef} style={canvasStyles} width={width} />;
};
BackgroundCanvas.displayName = "Background";
export const Background = React.memo(BackgroundCanvas);
